import { ArrowLeft, Download, PlayCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';

import { ContenidoProtegido, ImagenProtegida } from '@/components/ContenidoProtegido';
import { usePageEnter } from '@/lib/motion';
import { cn } from '@/lib/utils';

import {
  Cargando,
  ErrorCarga,
  Reproductor,
  TabsModulo,
  TextoMarcado,
  VisorLaminas,
} from '../curso/componentes';
import {
  useGuardarAvancePresentacion,
  usePresentacion,
  useResumenGpc,
  useVideotip,
  useVisorFlashcards,
  vistaFlashcard,
} from '../curso/queries';

import { ApoyosContenido } from './CasosClinicos';

function useId(): number {
  const { id } = useParams();
  return Number(id);
}

function Volver({ a, texto }: { a: string; texto: string }) {
  return (
    <Link
      to={a}
      className="inline-flex items-center gap-1.5 text-sm font-bold text-azul-texto hover:underline"
    >
      <ArrowLeft size={16} /> {texto}
    </Link>
  );
}

export function PresentacionPage() {
  const id = useId();
  const { data, error, isPending, refetch } = usePresentacion(id);
  const guardar = useGuardarAvancePresentacion(id);
  const espera = useRef<number | undefined>(undefined);
  const root = useRef<HTMLDivElement>(null);
  usePageEnter(root, [id]);
  return (
    <div ref={root} className="mx-auto max-w-screen-2xl space-y-5">
      <TabsModulo />
      <Volver a="/curso-enarm/biblioteca/presentaciones" texto="Presentaciones" />
      {isPending && <Cargando alto="h-96" />}
      {error && <ErrorCarga error={error} />}
      {data && (
        <>
          {/* Encabezado del legacy: «PRESENTACIÓN» en verde + rótulo azul con el nombre. */}
          <header className="text-center">
            <p className="text-sm font-extrabold tracking-widest text-verde-texto uppercase">
              Presentación
            </p>
            <h1 className="rotulo-pill mt-1 tracking-wide normal-case">{data.name}</h1>
            {data.description && <p className="mt-3 text-texto-2">{data.description}</p>}
          </header>
          <VisorLaminas
            titulo={data.name}
            contenido={['presentation', data.libraryId]}
            inicial={data.savedSlide}
            laminas={data.slides.map((s) => ({ clave: s.number, imageUrl: s.imageUrl }))}
            onCambio={(n) => {
              window.clearTimeout(espera.current);
              espera.current = window.setTimeout(() => {
                guardar.mutate(n);
              }, 600);
            }}
            onExpirada={() => refetch()}
          />
          <ApoyosContenido tipo="presentations" id={data.libraryId} />
        </>
      )}
    </div>
  );
}

/**
 * Visor de flashcards: hermanas de la especialidad en orden alfabético. Abre en la que eligió
 * el alumno y registra una vista por flashcard que se muestra.
 */
export function FlashcardPage() {
  const id = useId();
  const navigate = useNavigate();
  const { data, error, isPending, refetch } = useVisorFlashcards(id);
  const root = useRef<HTMLDivElement>(null);
  const [actualId, setActualId] = useState(id);
  usePageEnter(root, [data === undefined]);

  useEffect(() => {
    void vistaFlashcard(actualId);
  }, [actualId]);

  const inicial = data
    ? Math.max(
        data.items.findIndex((f) => f.flashcardId === id),
        0,
      ) + 1
    : 1;
  const actual = data?.items.find((f) => f.flashcardId === actualId);
  return (
    <div ref={root} className="mx-auto max-w-screen-xl space-y-5">
      <TabsModulo />
      <Volver a="/curso-enarm/biblioteca/flashcards" texto="Flash cards" />
      {isPending && <Cargando alto="h-96" />}
      {error && <ErrorCarga error={error} />}
      {data && (
        <>
          <header className="flex flex-wrap items-baseline justify-between gap-2">
            <h1 className="text-2xl font-extrabold text-azul-texto">
              <span className="mr-2 align-middle text-xs tracking-widest text-verde-texto uppercase">
                Flashcards
              </span>
              {actual?.name}
            </h1>
            <span className="pill-marca">
              {data.specialty}
              {data.subspecialty !== data.specialty ? ` · ${data.subspecialty}` : ''}
            </span>
          </header>
          {data.lastViewedId && data.lastViewedId !== id && (
            <p className="text-sm text-texto-2">
              La última que viste fue{' '}
              <button
                type="button"
                className="font-bold text-azul-texto underline"
                onClick={() => {
                  void navigate(`/curso-enarm/biblioteca/flashcards/${String(data.lastViewedId)}`);
                }}
              >
                {data.items.find((f) => f.flashcardId === data.lastViewedId)?.name}
              </button>
              .
            </p>
          )}
          <VisorLaminas
            key={`visor-${String(id)}`}
            titulo="Flashcard"
            contenido={['flashcard', actualId]}
            inicial={inicial}
            laminas={data.items.map((f) => ({
              clave: f.flashcardId,
              imageUrl: f.imageUrl,
              nombre: f.name,
            }))}
            onCambio={(n) => {
              const f = data.items[n - 1];
              if (f) setActualId(f.flashcardId);
            }}
            onExpirada={() => refetch()}
          />
          <ApoyosContenido key={`apoyos-${String(actualId)}`} tipo="flashcards" id={actualId} />
        </>
      )}
    </div>
  );
}

export function VideoTipPage() {
  const id = useId();
  const { data, error, isPending } = useVideotip(id);
  const root = useRef<HTMLDivElement>(null);
  usePageEnter(root, [id]);
  return (
    <div ref={root} className="mx-auto max-w-screen-xl space-y-5">
      <TabsModulo />
      <Volver a="/curso-enarm/biblioteca/video-tips" texto="Video tips" />
      {isPending && <Cargando alto="aspect-video" />}
      {error && <ErrorCarga error={error} />}
      {data && (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
          <div className="space-y-4">
            <h1 className="text-2xl font-extrabold text-azul-texto">{data.name}</h1>
            <ContenidoProtegido
              contenido={['videotip', data.libraryId]}
              className="rounded-[var(--radius-card)]"
            >
              <Reproductor embedUrl={data.embedUrl} titulo={data.name} />
            </ContenidoProtegido>
            <ApoyosContenido tipo="videotips" id={data.libraryId} />
          </div>
          <aside className="panel-vidrio self-start overflow-hidden p-0">
            <h2 className="bg-marca px-4 py-3 text-center text-sm font-extrabold text-white uppercase">
              {data.subspecialty}
            </h2>
            <ul>
              {data.playlist.map((v) => (
                <li key={v.libraryId}>
                  <Link
                    to={`/curso-enarm/biblioteca/video-tips/${String(v.libraryId)}`}
                    aria-current={v.libraryId === data.libraryId ? 'page' : undefined}
                    className={cn(
                      'flex items-center gap-2 px-4 py-3 text-sm transition-colors hover:bg-white/10',
                      v.libraryId === data.libraryId && 'bg-azul-600 font-bold text-white',
                    )}
                  >
                    <PlayCircle size={16} className="shrink-0" /> {v.name}
                  </Link>
                </li>
              ))}
            </ul>
          </aside>
        </div>
      )}
    </div>
  );
}

/** Resumen GPC (skill §7): bloques de título y contenido con las marcas E/R/BP/RC del legacy. */
export function ResumenGpcPage() {
  const id = useId();
  const { data, error, isPending, refetch } = useResumenGpc(id);
  const root = useRef<HTMLDivElement>(null);
  usePageEnter(root, [id]);
  return (
    <div ref={root} className="mx-auto max-w-4xl space-y-5">
      <TabsModulo />
      <Volver a="/curso-enarm/biblioteca/resumenes-gpc" texto="Resúmenes de guías GPC" />
      {isPending && <Cargando alto="h-96" />}
      {error && <ErrorCarga error={error} />}
      {data && (
        <>
          <header className="space-y-2 text-center">
            <span className="rotulo-pill rotulo-pill-verde text-xs">{data.specialty}</span>
            <h1 className="text-2xl font-extrabold text-verde-texto">{data.name}</h1>
            <p className="text-sm font-bold text-azul-texto uppercase">
              Resumen · Guía de práctica clínica (GPC)
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {data.fullGuideUrl && (
                <a
                  href={data.fullGuideUrl}
                  target="_blank"
                  rel="noopener"
                  className="btn-cs btn-contorno"
                >
                  <Download size={16} /> Guía completa
                </a>
              )}
              {data.shortGuideUrl && (
                <a
                  href={data.shortGuideUrl}
                  target="_blank"
                  rel="noopener"
                  className="btn-cs btn-contorno"
                >
                  <Download size={16} /> Guía de referencia rápida
                </a>
              )}
            </div>
          </header>
          <ContenidoProtegido
            contenido={['gpc_summary', data.libraryId]}
            className="rounded-[var(--radius-card)]"
          >
            <article className="superficie-lectura marca-agua space-y-3 p-5 leading-relaxed sm:p-7">
              <LeyendaMarcas />
              {data.blocks.map((b) =>
                b.type === 'titulo' ? (
                  <h2 key={b.id} className="barra-gpc mt-4 first:mt-0">
                    {b.title}
                  </h2>
                ) : b.type === 'imagen' && b.imageUrl ? (
                  <ImagenProtegida
                    key={b.id}
                    src={b.imageUrl}
                    clave={b.id}
                    alt={b.title ?? 'Imagen del resumen'}
                    onExpirada={() => refetch()}
                  />
                ) : (
                  <div
                    key={b.id}
                    className="flex gap-3 rounded-lg p-2"
                    style={
                      b.backgroundColor
                        ? { background: b.backgroundColor, color: b.textColor ?? undefined }
                        : undefined
                    }
                  >
                    <Marcas b={b} />
                    <p className="flex-1">
                      <TextoMarcado texto={b.text ?? ''} />
                    </p>
                  </div>
                ),
              )}
            </article>
          </ContenidoProtegido>
        </>
      )}
    </div>
  );
}

const MARCAS = [
  { t: 'E', titulo: 'Evidencia', c: 'bg-azul-600' },
  { t: 'R', titulo: 'Recomendación', c: 'bg-verde-700' },
  { t: 'BP', titulo: 'Buena práctica', c: 'bg-morado-700' },
  { t: 'RC', titulo: 'Recomendación clave', c: 'bg-rojo-600' },
] as const;

/** Leyenda de las marcas, como el recuadro lateral del resumen legacy. */
function LeyendaMarcas() {
  return (
    <ul className="flex flex-wrap justify-center gap-x-4 gap-y-1 border-b border-[var(--separador)] pb-3 text-xs">
      {MARCAS.map((m) => (
        <li key={m.t} className="flex items-center gap-1.5">
          <span
            className={cn(
              'grid h-5 min-w-5 place-items-center rounded px-1 font-extrabold text-white',
              m.c,
            )}
          >
            {m.t}
          </span>
          {m.titulo}
        </li>
      ))}
    </ul>
  );
}

function Marcas({
  b,
}: {
  b: {
    evidence: boolean;
    recommendation: boolean;
    goodPractice: boolean;
    keyRecommendation: boolean;
  };
}) {
  const activas = [b.evidence, b.recommendation, b.goodPractice, b.keyRecommendation];
  const marcas = MARCAS.filter((_, i) => activas[i]);
  if (marcas.length === 0) return null;
  return (
    <span className="flex shrink-0 flex-col gap-1">
      {marcas.map((m) => (
        <abbr
          key={m.t}
          title={m.titulo}
          className={cn(
            'grid h-7 min-w-7 place-items-center rounded-md px-1 text-xs font-extrabold text-white no-underline',
            m.c,
          )}
        >
          {m.t}
        </abbr>
      ))}
    </span>
  );
}
